import React, { useMemo, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  Table,
  TableFilter,
  Flex,
  BoxV2 as Box,
  useFilters,
  useTable,
} from 'portal-commons';

import {
  FILTER_CONFIG,
  TABLE_COLUMNS,
  FILTER_LOADERS,
  PAGE_SIZE,
} from './constants';
import { campaignController, cspController } from '@/apis/core';
import { Loader, FilterToggleButton } from '@/shared_elements';
import { convertTimeWithTimezone, processDateRangeQuery } from '@/utils/time';
import { formatUSPhoneNumber } from '@/utils/phone';
import { createCampaignRedirectorPath } from '@/utils/path';
import { PhoneNumberHistory } from '@/apis/core/types';

const NumberHistoryList: React.FC = () => {
  const { phone } = useParams<{ phone: string }>();
  const history = useHistory();
  const location = useLocation();
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const filter = useFilters({
    configs: FILTER_CONFIG,
    loaders: FILTER_LOADERS,
  });

  const getQueries = () => {
    const queries = {
      ...table.filter,
      ...filter.appliedFilters,
      ...filter.additionalFilters,
    };

    // date range pre-processing
    processDateRangeQuery(queries, 'dateRange', 'startDate', 'endDate');

    // boolean conversion for 'current'
    if (queries.current === 'true' || queries.current === true) {
      queries.current = true;
    } else if (queries.current === 'false' || queries.current === false) {
      queries.current = false;
    } else {
      delete queries.current;
    }

    return queries;
  };

  const resolveCspUidFromQueries = async (
    companyName?: string,
    displayName?: string
  ): Promise<{ cspUid?: string; shouldReturnEmpty: boolean }> => {
    if (!companyName && !displayName) {
      return { shouldReturnEmpty: false };
    }

    const [companySuggestions, displaySuggestions] = await Promise.all([
      companyName
        ? cspController.getCspSuggestions({
            companyNamePrefix: companyName,
            limit: 10,
          })
        : Promise.resolve([]),
      displayName
        ? cspController.getCspSuggestions({ prefix: displayName, limit: 10 })
        : Promise.resolve([]),
    ]);

    const companyNameCspUid = companyName
      ? companySuggestions.find((s) => s.cspCompanyName === companyName)?.cspUid
      : undefined;

    const displayNameCspUid = displayName
      ? displaySuggestions.find((s) => s.cspName === displayName)?.cspUid
      : undefined;

    // Return empty if any provided query doesn't match a valid CSP UID, or if both IDs mismatch
    if (companyName && !companyNameCspUid) return { shouldReturnEmpty: true };
    if (displayName && !displayNameCspUid) return { shouldReturnEmpty: true };
    if (companyName && displayName && companyNameCspUid !== displayNameCspUid) {
      return { shouldReturnEmpty: true };
    }

    return {
      cspUid: companyNameCspUid ?? displayNameCspUid,
      shouldReturnEmpty: false,
    };
  };

  const table = useTable<PhoneNumberHistory>(
    async () => {
      const queries = getQueries();

      // Omit companyName and displayName from API params
      const { companyName, displayName, ...apiParams } = queries as Record<
        string,
        string | undefined
      >;

      const { cspUid, shouldReturnEmpty } = await resolveCspUidFromQueries(
        companyName,
        displayName
      );

      if (shouldReturnEmpty) {
        return { data: [], page: 1, rowsPerPage: PAGE_SIZE, total: 0 };
      }

      if (cspUid) {
        apiParams.cspUid = cspUid;
      }

      const response = await campaignController.getPhoneNumberHistory(
        phone,
        apiParams
      );

      return {
        data: response?.records ?? [],
        page: response?.page ?? 1,
        rowsPerPage: response?.recordsPerPage ?? PAGE_SIZE,
        total: response?.totalRecords ?? 0,
      };
    },
    {
      deps: [filter.appliedFilters, filter.additionalFilters, phone],
      defaultFilter: {
        sortField: 'startTimestamp',
        ascendingOrder: false,
      },
    }
  );

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prev) => !prev);
  };

  const handleCampaignRedirect = (campaignUid: string) => {
    history.push({
      pathname: createCampaignRedirectorPath(campaignUid),
      state: {
        goBackPage: `${location.pathname}${
          location.search ? location.search : ''
        }`,
      },
    });
  };

  const tableData = useMemo(() => {
    return table.tableData.map((record: PhoneNumberHistory) => ({
      ...record,
      campaignUid: record.campaignUid ? (
        <Box
          role="button"
          data-testid="campaignUidLink"
          sx={{
            cursor: 'pointer',
            '&:hover': {
              color: 't.blue100',
              fontWeight: 'bold',
            },
          }}
          onClick={(e) => {
            e.preventDefault();
            record.campaignUid && handleCampaignRedirect(record.campaignUid);
          }}
        >
          {record.campaignUid}
        </Box>
      ) : (
        'N/A'
      ),
      cspDbaName: record.cspDbaName || 'N/A',
      cspLegalName: record.cspLegalName || 'N/A',
      nnid: record.nnid || 'N/A',
      current: record.current ? 'Yes' : 'No',
      startTimestamp: record.startTimestamp
        ? convertTimeWithTimezone(record.startTimestamp, 'MM/DD/YYYY')
        : 'N/A',
      endTimestamp: record.endTimestamp
        ? convertTimeWithTimezone(record.endTimestamp, 'MM/DD/YYYY')
        : 'N/A',
    }));
  }, [table.tableData]);

  React.useEffect(() => {
    if (/[a-zA-Z]/.test(phone)) {
      history.replace('/404');
    } else {
      document.title = `The Campaign Registry - Number History`;
    }
  }, [phone, history]);

  const handleCandidateChange = (newValues: Record<string, string>) => {
    filter.handleEdit(newValues);
  };

  const handleAppliedChange = (newValues: Record<string, string>) => {
    filter.handleApply(newValues);
  };

  return (
    <section
      className="numbers-listing-section"
      style={{ padding: '0px' }}
      data-testid="numberHistoryList"
    >
      {table.pageLoading ? (
        <Loader />
      ) : (
        <>
          <Flex
            sx={{
              alignItems: 'center',
              mb: '16px',
              fontWeight: 600,
              fontSize: 'H600',
              lineHeight: '32px',
              color: 't.black80',
            }}
          >
            <span>{formatUSPhoneNumber(phone)} – Phone Number History</span>
          </Flex>

          <Flex sx={{ alignItems: 'center', gap: 's', mb: 'xs' }}>
            <FilterToggleButton
              visible={isFilterVisible}
              onClick={toggleFilterVisibility}
            />
          </Flex>

          <Box
            sx={{
              maxWidth: '100%',
              padding: 0,
              '& .MuiAutocomplete-root': {
                width: 'auto',
              },
              '& [data-testid="FilterItemDateRangePicker"].focus label, & [data-testid="FilterItemDateRangePicker"].disabled label':
                {
                  transform: 'translate(-26px,-22px) scale(0.78)',
                },
            }}
          >
            {isFilterVisible && (
              <TableFilter
                portal="dca"
                configs={filter.configs}
                candidateValues={filter.candidateFilters}
                appliedValues={filter.appliedFilters}
                onCandidateValuesChange={handleCandidateChange}
                onAppliedValuesChange={handleAppliedChange}
                data-testid="numberHistoryTableFilter"
              />
            )}

            <Flex
              sx={{
                flexDirection: 'column',
                justifyContent: 'center',
                mt: 'm',
              }}
            >
              <Table
                testId="numberHistoryTable"
                disableHover
                headRows={TABLE_COLUMNS}
                data={tableData}
                loading={table.loading}
                emptyState="no number history to view"
                handleChangePage={table.handleChangePage}
                createSortHandler={table.handleSorting}
                filter={table.filter}
                pagination={table.pagination}
              />
            </Flex>
          </Box>
        </>
      )}
    </section>
  );
};

export default NumberHistoryList;
